import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SideMenuNode, SideMenuSelectEvent } from '../../../../shared/components/sidemenu/sidemenu.model';
import { AskPanelComponent } from '../../ask-panel/ask-panel.component';
import { JsonEditorComponent } from '../../../../shared/elements/json-editor/json-editor.component';
import {
  VisualElementDraft,
  DEFAULT_VISUAL_ORDER,
  DEFAULT_VISUAL_WIDTH,
  buildVisualJson,
  isVisualElementValid,
  parseDatasets,
  parseVisualJson,
  getDefaultCountColumn
} from '../add-visual/add-visual.model';

export interface VisualDraft {
  title: string;
  width: number;
  order: number;
}

const EMPTY_VISUAL_DRAFT: VisualDraft = { title: '', width: DEFAULT_VISUAL_WIDTH, order: DEFAULT_VISUAL_ORDER };

/**
 * Body of the "Add visual" popup - same ASK / Visual Editor / JSON Editor / SQL Query shell as
 * AddFilterModalComponent, reused for every visual type; only the header label changes per type
 * (set via `visualTypeLabel` from the grid item the user clicked in InbuiltCustomReportComponent).
 */
@Component({
  selector: 'app-add-visual-modal',
  standalone: false,
  templateUrl: './add-visual-modal.component.html',
  styleUrl: './add-visual-modal.component.scss'
})
export class AddVisualModalComponent implements OnChanges {
  @ViewChild('askPanel') private askPanel?: AskPanelComponent;
  @ViewChild('visualJsonEditor') private visualJsonEditor?: JsonEditorComponent;
  @Input() visualTypeLabel = '';
  @Input() selectedElementType = '';
  @Input() reportId: string | number | null = null;
  // Report's datasource config, forwarded to the Visual Editor's column pickers.
  @Input() selectedDataSource: any = null;
  // Names of the filters on the report page - the options of "Applied filters".
  @Input() availableFilters: string[] = [];
  @Input() filterElementJson: any = null;
  @Input() currentVisualCount = 0;
  // Gates the optional "Add link to key" (valuesLink) control in the Visual Editor.
  @Input() hasReportAdminRole = false;
  @Output() cancelled = new EventEmitter<void>();
  @Output() visualCreated = new EventEmitter<any>();

  readonly menuItems: SideMenuNode[] = [
    { id: 'ask', type: 'ask', name: 'ASK' },
    { id: 'visual-editor', type: 'editor', name: 'Visual Editor' },
    { id: 'json-editor', type: 'editor', name: 'JSON Editor' },
    { id: 'sql-query', type: 'query', name: 'SQL Query' }
  ];
  selectedMenuId = 'ask';

  askPromptValue = '';
  draft: VisualDraft = { ...EMPTY_VISUAL_DRAFT };
  // Element config edited in the Visual Editor - an array so more elements can be supported later.
  visualElements: VisualElementDraft[] = [];
  jsonEditorInitialJson: any = {};
  sqlQueryCode = '';
  visualValidationPassed = false;
  private chartOrderCustomized = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentVisualCount'] && !this.chartOrderCustomized) {
      this.draft.order = this.getDefaultVisualOrder();
    }
  }

  get modalTitle(): string {
    return this.visualTypeLabel ? `Add ${this.visualTypeLabel}` : 'Add visual';
  }

  onMenuSelected(event: SideMenuSelectEvent): void {
    if (event?.node?.id) {
      this.selectedMenuId = event.node.id;
      if (this.selectedMenuId === 'json-editor') { this.syncVisualEditorToJson(); }
    }
  }

  onVisualDraftChange(elements: VisualElementDraft[]): void {
    this.visualElements = elements;
    this.sqlQueryCode = '';
    this.visualValidationPassed = false;
    if (this.selectedMenuId === 'visual-editor') {
      this.syncVisualEditorToJson(true);
    }
  }

  onVisualHeadingChange(heading: string): void {
    this.draft.title = heading;
    this.visualValidationPassed = false;
    if (this.selectedMenuId === 'visual-editor') {
      this.syncVisualEditorToJson(true);
    }
  }

  onChartOrderChange(order: number): void {
    this.draft.order = order;
    this.chartOrderCustomized = true;
  }

  onChartWidthChange(width: number): void {
    this.draft.width = width;
  }

  // ASK -> JSON Editor / Visual Editor: once ASK returns a result, show elementJson in the JSON Editor
  // and generatedSql in the SQL Query tab, and rebuild the Visual Editor fields from elementJson.
  onAskGenerated(response: any): void {
    const elementJson = this.parseElementJson(response?.elementJson);
    const sql = typeof response?.generatedSql === 'string'
      ? response.generatedSql
      : response?.generatedSql == null ? '' : String(response.generatedSql);

    if (elementJson && typeof elementJson === 'object' && Object.keys(elementJson).length) {
      this.jsonEditorInitialJson = elementJson;
      const elements = parseVisualJson(elementJson, parseDatasets(this.selectedDataSource));
      if (elements.length) {
        const datasets = parseDatasets(this.selectedDataSource);
        elements.forEach(element => {
          if (!element.totalCountColumn) {
            element.totalCountColumn = getDefaultCountColumn(element, datasets);
          }
        });
        this.visualElements = elements;
      }
    }
    this.sqlQueryCode = sql;
    this.visualValidationPassed = false;
    // Pushing new content into the (already-mounted, hidden) JSON Editor re-fires its own
    // jsonTextChange/constructedQueryChange listeners, which reset sqlQueryCode to '' as a side
    // effect of that synthetic edit - reassert the ASK-provided SQL once that settles.
    setTimeout(() => { this.sqlQueryCode = sql; });
  }

  private parseElementJson(value: any): any {
    if (typeof value !== 'string') { return value; }
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  // JSON Editor -> Visual Editor: once the JSON is validated, rebuild the UI rows from it.
  onJsonValidated(jsonText: string): void {
    if (this.selectedMenuId === 'visual-editor') {
      this.visualValidationPassed = true;
      return;
    }
    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      return;
    }
    const elements = parseVisualJson(parsed, parseDatasets(this.selectedDataSource));
    if (elements.length) {
      this.visualElements = elements;
      this.visualValidationPassed = true;
    }
  }

  onConstructedQueryChange(query: any): void {
    this.sqlQueryCode = typeof query === 'string'
      ? query
      : query == null ? '' : this.toQueryText(query);
  }

  // Visual Editor -> JSON Editor. Skipped on tab switch when the JSON was hand-edited, so those edits aren't overwritten.
  private syncVisualEditorToJson(force = false): void {
    if ((!force && this.visualJsonEditor?.isChanged === true) ||
      !this.visualElements.length) {
      return;
    }
    const updatedJson = this.withDynamicTitleHeader(this.buildVisualJsonWithFilters());
    if (JSON.stringify(updatedJson) !== JSON.stringify(this.jsonEditorInitialJson)) {
      this.jsonEditorInitialJson = updatedJson;
    }
  }

  get appliedFilterOptions(): string[] {
    const labels = Array.isArray(this.parsedFilterElementJson?.fields)
      ? this.parsedFilterElementJson.fields.map((field: any) => field?.label).filter((label: any) => typeof label === 'string' && !!label.trim())
      : [];
    return [...new Set([...this.availableFilters, ...labels])];
  }

  private get parsedFilterElementJson(): any {
    if (typeof this.filterElementJson !== 'string') { return this.filterElementJson; }
    try { return JSON.parse(this.filterElementJson); } catch { return null; }
  }

  private buildVisualJsonWithFilters(): any {
    const visualJson = buildVisualJson(this.visualElements, parseDatasets(this.selectedDataSource));
    const filterKeys = this.parsedFilterElementJson?.query_filter?.columnkey;
    if (!filterKeys || typeof filterKeys !== 'object' || Array.isArray(filterKeys)) { return visualJson; }
    const merge = (visual: any) => ({
      ...visual,
      columnkey: { ...filterKeys, ...visual.columnkey }
    });
    return Array.isArray(visualJson) ? visualJson.map(merge) : merge(visualJson);
  }

  private withDynamicTitleHeader(elementJson: any): any {
    const title = this.draft.title.trim();
    const titleKey = title.toLowerCase().split(/[\s_]+/).filter(Boolean).join('_');
    if (!titleKey || !elementJson || typeof elementJson !== 'object' || Array.isArray(elementJson)) {
      return elementJson;
    }
    return { ...elementJson, dynamicTitleHeader: { [`{${titleKey}}`]: title } };
  }

  private toQueryText(query: any): string {
    try {
      return typeof query === 'object' ? JSON.stringify(query, null, 2) : String(query);
    } catch {
      return String(query);
    }
  }

  private getElementJson(): any {
    if (this.selectedMenuId !== 'json-editor' && this.visualElements.length && this.visualElements.every(isVisualElementValid)) {
      return this.withDynamicTitleHeader(this.buildVisualJsonWithFilters());
    }
    const editorValue = this.visualJsonEditor?.getJSON() ?? this.jsonEditorInitialJson;
    if (typeof editorValue !== 'string') { return editorValue; }
    try {
      return JSON.parse(editorValue);
    } catch {
      return {};
    }
  }

  // app-modal keeps this component mounted between opens (see select-datasource-modal-pattern memory) -
  // the host must call this before showing the modal again so a previous draft isn't still here.
  resetState(): void {
    this.selectedMenuId = 'ask';
    this.askPromptValue = '';
    this.chartOrderCustomized = false;
    this.draft = { ...EMPTY_VISUAL_DRAFT, order: this.getDefaultVisualOrder() };
    this.visualElements = [];
    this.askPanel?.resetState();
    this.jsonEditorInitialJson = {};
    this.sqlQueryCode = '';
    this.visualValidationPassed = false;
  }

  onJsonTextChange(value: string): void {
    if (typeof value !== 'string') { return; }
    this.visualValidationPassed = false;
    this.sqlQueryCode = '';
  }

  get primaryActionLabel(): string {
    return this.selectedMenuId === 'ask' ? 'Generate' : 'Add visual';
  }

  get showPrimaryAction(): boolean {
    if (this.selectedMenuId === 'ask') { return !!this.askPromptValue.trim(); }
    return (this.selectedMenuId === 'visual-editor' || this.selectedMenuId === 'json-editor') &&
      this.visualValidationPassed && !!this.draft.title.trim();
  }

  get canValidate(): boolean {
    if (this.visualValidationPassed) { return false; }
    if (this.selectedMenuId === 'visual-editor') {
      return !!this.draft.title.trim() && this.visualElements.length > 0 &&
        this.visualElements.every(isVisualElementValid);
    }
    if (this.selectedMenuId === 'json-editor') {
      return !!this.draft.title.trim() && this.hasNonEmptyJson(this.visualJsonEditor?.getJSON());
    }
    return false;
  }

  get canPerformPrimaryAction(): boolean {
    if (this.selectedMenuId === 'ask') {
      return this.askPanel?.hasReportId === true && !!this.askPromptValue.trim() && !this.askPanel?.generating;
    }
    if (!this.draft.title.trim()) { return false; }
    return (this.selectedMenuId === 'visual-editor' || this.selectedMenuId === 'json-editor') &&
      this.visualValidationPassed;
  }

  onValidate(): void {
    if (!this.canValidate || !this.visualJsonEditor) { return; }
    this.visualValidationPassed = false;
    if (this.selectedMenuId === 'visual-editor') {
      const elementJson = this.withDynamicTitleHeader(this.buildVisualJsonWithFilters());
      this.jsonEditorInitialJson = elementJson;
      this.visualJsonEditor.setJSON(elementJson);
    }
    this.visualJsonEditor.validateJson();
  }

  private hasNonEmptyJson(value: any): boolean {
    if (value == null) { return false; }
    if (typeof value === 'string') {
      const text = value.trim();
      if (!text) { return false; }
      try { value = JSON.parse(text); } catch { return false; }
    }

    return !!value && typeof value === 'object' &&
      (Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0);
  }

  private getDefaultVisualOrder(): number {
    const visualCount = Math.max(0, Math.trunc(Number(this.currentVisualCount) || 0));
    return visualCount === 0 ? DEFAULT_VISUAL_ORDER : visualCount + DEFAULT_VISUAL_ORDER;
  }

  onPrimaryAction(): void {
    if (this.selectedMenuId === 'ask') {
      this.askPanel?.generateReport();
      return;
    }
    this.visualCreated.emit({
      draft: this.draft,
      elementJson: this.getElementJson(),
      jsonEditor: this.jsonEditorInitialJson,
      sqlQuery: this.sqlQueryCode
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }
}
